import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Dot, PopoverMenu, PopoverMenuItemDropdown, Text, usePopoverMenu } from '@frontend/design-system';
import { CheckboxGroup } from './checkbox-group';
import { ChartEditPanelProps } from './types';
import { XAxisSelector } from './x-axis-selector';

export const ChartEditPanel = ({
  yAxisGroups,
  defaultYAxisSelected = {},
  xAxisOptions,
  xAxisLabel,
  defaultXAxisSelected,
  onYChange,
  onXChange,
}: ChartEditPanelProps) => {
  const { t } = useTranslation('analytics');
  const hasXAxisOptions = !!xAxisOptions?.length;
  const hasYAxisGroups = !!yAxisGroups?.length;
  const resolvedDefaultX = defaultXAxisSelected ?? xAxisOptions?.[0]?.value ?? '';

  const { getTriggerProps, getMenuProps, getItemProps, close, isOpen } = usePopoverMenu({
    placement: 'bottom-end',
  });

  const flatDefaultY = Object.values(defaultYAxisSelected).flat();
  const confirmedYRef = useRef<string[]>(flatDefaultY);
  const [draftY, setDraftY] = useState<string[]>(flatDefaultY);
  const [selectedX, setSelectedX] = useState<string>(resolvedDefaultX);
  const [confirmedCount, setConfirmedCount] = useState<number>(flatDefaultY.length);

  // Reset draft to last confirmed when popover is dismissed without applying
  const prevIsOpenRef = useRef(isOpen);

  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen) {
      setDraftY(confirmedYRef.current);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  const handleViewClick = useCallback(() => {
    if (yAxisGroups) {
      // Transform from draftY array of selected values to the grouped object expected by onYChange
      const selectedYByGroup = Object.fromEntries(
        yAxisGroups.map((group) => [group.key, draftY.filter((v) => group.options.some((o) => o.value === v))])
      );
      confirmedYRef.current = draftY;
      setConfirmedCount(draftY.length);
      close();
      onYChange(selectedYByGroup, selectedX);
    }
  }, [close, draftY, selectedX, onYChange, yAxisGroups]);

  const handleXChange = useCallback(
    (x: string) => {
      setSelectedX(x);
      onXChange?.(x);
    },
    [onXChange]
  );

  if (!hasXAxisOptions && !hasYAxisGroups) {
    return null;
  }

  return (
    <>
      <div css={styles.controls}>
        {hasXAxisOptions && xAxisOptions && (
          <XAxisSelector options={xAxisOptions} value={selectedX} onChange={handleXChange} label={xAxisLabel} />
        )}
        {hasYAxisGroups && (
          <Button
            {...getTriggerProps()}
            iconName='filter-alt'
            label={t('Edit')}
            size='small'
            css={styles.openEditConfigButton}
            variant='secondary'
            hoverLabel={t('Chart Filters')}
          >
            {confirmedCount > 0 && (
              <Dot
                color='primary'
                css={{
                  width: theme.spacing(1),
                  height: theme.spacing(1),
                  top: 6,
                  right: 8,
                  border: `1.5px solid ${theme.colors.white}`,
                }}
              />
            )}
          </Button>
        )}
      </div>
      {hasYAxisGroups && (
        <PopoverMenu {...getMenuProps()} css={styles.popoverMenu}>
          <div css={styles.popoverHeader}>
            <Text weight='bold'>{t('Chart Filters')}</Text>
            <Button
              variant='tertiary'
              size='small'
              onClick={() => setDraftY([])}
              disabled={draftY.length === 0}
              trackingId='chart-edit-panel-clear-filters-button'
            >
              {t('Clear')}
            </Button>
          </div>
          {yAxisGroups?.map((group, index) => {
            const selectedCount = draftY.filter((v) => group.options.some((o) => o.value === v)).length;
            const IconComponent = group.icon ? () => <Icon name={group.icon as IconName} size={16} /> : () => null;
            return (
              <PopoverMenuItemDropdown
                key={group.label}
                showOnHover
                {...getItemProps({ index, disableCloseOnSelect: true })}
                PrefixIcon={IconComponent}
                popoverProps={{ placement: 'left-start' }}
                subMenuItems={() => (
                  <div css={styles.subMenuContent}>
                    <CheckboxGroup group={group} draftY={draftY} onCheckboxChange={setDraftY} />
                  </div>
                )}
              >
                <div css={styles.groupRow}>
                  {group.label}
                  {selectedCount > 0 && <div css={styles.countBadge}>{selectedCount}</div>}
                </div>
              </PopoverMenuItemDropdown>
            );
          })}
          <div css={styles.applyButtonContainer}>
            <Button onClick={handleViewClick} css={styles.applyButton}>
              {t('Apply')}
            </Button>
          </div>
        </PopoverMenu>
      )}
    </>
  );
};

const styles = {
  openEditConfigButton: css({
    height: theme.spacing(4),
    border: `1px solid ${theme.colors.neutral30}`,
    padding: theme.spacing(1),
  }),
  popoverMenu: css({
    minWidth: 280,
    padding: 0,
  }),
  popoverHeader: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  }),
  groupRow: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  }),
  countBadge: css({
    float: 'right',
    padding: `0 ${theme.spacing(0.5)}`,
    borderRadius: theme.borderRadius.small,
    backgroundColor: theme.colors.primary10,
    color: theme.colors.primary70,
    fontSize: theme.fontSize(12),
  }),
  subMenuContent: css({
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    minWidth: 180,
  }),
  applyButtonContainer: css({
    padding: `${theme.spacing(2)}`,
  }),
  applyButton: css({
    width: '100%',
  }),
  controls: css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  }),
};
